/**
 * CopyCampaignWorkflow — API layer.
 *
 * REAL calls (already-shipped backend routes, wired here for real):
 *   - fetchSourceCampaign      GET  /api/v2/campaigns/:id
 *   - copyCampaign             POST /api/v2/campaigns/copy
 *   - interpretCopyInstruction POST /api/v2/campaigns/copy/intent
 *   - fetchMessageStatus       GET  /api/v2/message/:id/status
 *   - fetchPlanningReview      GET  /api/v2/campaigns/:id/planning-review
 *   - savePlanningReview       PUT  /api/v2/campaigns/:id/planning-review
 *   - launchPlanningCampaign   POST /api/v2/campaigns/:id/planning-launch
 * (fetchMessageStatus mirrors CampaignDetailPage.js's startMessagePolling —
 * same endpoint, same params shape — rather than inventing a new mechanism.
 * The planning-mode build poll and interpretCopyInstruction's own internal
 * poll below both follow the same interval-poll-until-terminal shape, just
 * against a different messageId/action.)
 *
 * Gates are not computed client-side: POST /copy with planningMode runs
 * campaign_editor/copy_gates.py against the REAL built template and the rows
 * ride back on launcherResult.gates (see GET /planning-review). This file
 * keeps only `resolveGates` — the pure client-side re-check that clears a
 * gate row once the user's grid edits actually fixed the flagged value.
 *
 * (The per-platform PREVIEW extractors that used to live here served only
 * the removed "Confirm what's copied" picker step and were deleted with it —
 * POST /copy/preview still exists server-side but has no frontend caller.)
 */
import { api, getApiUrl } from '../../utils/apiConfig';
// Pure template helpers (no transport) — used by computeUniqueCopyRename
// below. One-directional: copyReviewEdits.js never imports this module.
import {
  renameCampaignInLauncherResult, campaignNamesFromLauncherResult,
} from './copyReviewEdits';
// Shared money formatter — the same one the Confirm & adjust grid's 'cur' cells use, so
// step 2 and step 3 can't render one budget two different ways.
import { fmtMoney } from '../CampaignLaunchWorkflow/clwFormatters';

// ─── Platform normalization (mirrors normalizeCampaignCopyPlatform in
//     apps/app-backend/routes/v2/campaigns.js — kept in sync by hand since
//     there is no shared frontend/backend module for it) ───────────────────
const GOOGLE_SEARCH_DESTINATIONS = new Set([
  'google_search', 'google-ads-search', 'google-search', 'g-ads-search', 'google_ads_search',
]);
const GOOGLE_PMAX_DESTINATIONS = new Set([
  'google_pmax', 'google-ads-pmax', 'google-pmax', 'g-ads-pmax', 'g_ads_pmax',
  'google_ads_pmax', 'pmax',
]);
export function normalizeCopyPlatform(destination) {
  if (!destination || typeof destination !== 'string') return null;
  const lower = destination.trim().toLowerCase();
  if (GOOGLE_SEARCH_DESTINATIONS.has(lower)) return 'google_search';
  if (GOOGLE_PMAX_DESTINATIONS.has(lower)) return 'google_pmax';
  if (lower === 'dv360') return 'dv360';
  if (lower === 'ttd') return 'ttd';
  if (lower === 'facebook' || lower === 'fb' || lower === 'meta') return 'facebook';
  return null;
}

// ─── Shared small-chip platform label/color, for the compact `.ch` product
//     chips used across this workflow (CopyDescribeStep/CopyPushStep) —
//     single source so the steps stay in sync instead of each carrying its
//     own copy. Keyed by normalizeCopyPlatform's output — always look up via
//     that function first, never index by a raw `destination` value
//     directly. ─────────────────────────────────────────────────────────────
// NOTE: 'ch-pmax' is a COLOR class from the mock's palette, not a platform
// binding — DV360 has used it since the port. Google PMax reuses it rather than
// stealing it, since the two never appear as different rows of the same copy.
// Google Ads campaign types (Search + PMax). They live in a Google Ads ACCOUNT,
// so their copy payload keys off `customerId`, not `advertiserId` — sending the
// wrong one leaves campaign_copy_handler.py with no customer_id and fails the
// copy before it starts. Mirrors the same branch in apps/app-backend/routes/v2/
// campaigns.js's buildCampaignCopyMessageData.
export function isGoogleAdsCopyPlatform(platform) {
  return platform === 'google_search' || platform === 'google_pmax';
}

// Every platform the copy workflow supports, in the order the picker's filter
// chips show them. Single source so a new platform doesn't have to be added to
// each step's own hardcoded list.
export const COPY_PLATFORMS = ['google_search', 'google_pmax', 'dv360', 'ttd', 'facebook'];

/**
 * HIDDEN, NOT REMOVED — the natural-language describe/interpret surface.
 *
 * Flip to true to bring back, for every platform at once:
 *   - Describe step: the "Describe the copy" prompt card (textarea, hint
 *     chips, Interpret button), the intent selector + its guardrail note,
 *     the interpreted op list and its "N of M operations will run" footer,
 *     the interpreter's warnings banner, and the "Set the parameters
 *     manually instead" toggle (with it hidden, the same fields are simply
 *     always shown);
 *   - Adjust/Push steps: the "Re-describe" button in the workflow header.
 *
 * Everything behind this flag is INTACT and still wired end to end: the
 * intent handler, copy_intent_reasoning, structured field_changes ops and
 * copy_op_applier all still work — the UI just doesn't offer them. With it
 * off the build payload carries no prompt and no appliedOps, which both
 * layers already treat as "nothing to apply" (the flow has always supported
 * a purely manual describe step; it was the documented escape hatch).
 */
export const SHOW_COPY_INTERPRET_UI = false;

/**
 * Step-1 copy, which differs by mode because the step itself does.
 *
 * With the interpret UI ON, step 1 is "describe it in a sentence and I'll
 * turn it into an op list" — hence "What are you doing?" and a lede about
 * editable/declinable operations. With it OFF there is no sentence and no op
 * list: the step is just the source campaigns plus the flight/budget the
 * copies will use, so that wording described a surface the user cannot see.
 * Kept beside the flag so the two can never drift.
 */
export const COPY_STEP1_LABEL = SHOW_COPY_INTERPRET_UI
  ? 'What are you doing?'
  : 'Set up the copy';
export const COPY_STEP1_LEDE = SHOW_COPY_INTERPRET_UI
  ? "One sentence, then one list. What I'm copying, what guardrails apply, and every change I'll make — each one editable, declinable, or waiting on a value from you."
  : 'Confirm the campaign being copied and provide new settings. All remaining settings carries over from the source campaign.';
export const COPY_STEP1_CARD_TITLE = SHOW_COPY_INTERPRET_UI ? 'What will change' : 'What gets copied';
export const COPY_STEP1_CARD_SUB = SHOW_COPY_INTERPRET_UI
  ? "everything editable · uncheck anything you don't want"
  : 'the campaign being copied, and the settings the copy will use';

/**
 * Why a campaign row can't be copied, or null when it's eligible.
 *
 * FAIL EARLY: this is the single source of copy eligibility for every entry
 * point (CampaignsPage's checkbox column + row kebab, CopyCampaignEntryPicker)
 * so ineligible campaigns are blocked AT SELECTION with the reason on the
 * tooltip — not one step later as a failed build. The three rules mirror
 * exactly what the build would fail on anyway:
 *  - no channel_id            → never created in the DSP, nothing to copy from;
 *  - no platform_campaign_id  → a draft that hasn't been pushed: a copy imports
 *                               the LIVE campaign, so until launch there is
 *                               nothing on the platform to import (this is the
 *                               "Missing platform campaign id" build failure);
 *  - unsupported destination  → no copy launcher exists for the platform
 *                               (backend mirror: CAMPAIGN_COPY_PLATFORMS).
 */
export function copyIneligibilityReason(campaign) {
  if (!campaign) return 'No campaign.';
  if (!campaign.channel_id) {
    return 'This campaign has no DSP channel — it was never created in the DSP, so it can\'t be copied.';
  }
  if (!campaign.platform_campaign_id) {
    return 'This campaign hasn\'t been pushed to the platform yet — a copy imports the live campaign, so there\'s nothing to copy until it launches.';
  }
  if (!normalizeCopyPlatform(campaign.destination)) {
    return 'Copying isn\'t supported for this platform yet — Google Search, Performance Max, DV360, TTD and Facebook only.';
  }
  return null;
}

export const PLATFORM_CHIP_CLASS = {
  google_search: 'ch-srch', google_pmax: 'ch-pmax', dv360: 'ch-pmax', ttd: 'ch-dgen', facebook: 'ch-fb',
};
export const PLATFORM_CHIP_LABEL = {
  google_search: 'Search', google_pmax: 'PMax', dv360: 'DV360', ttd: 'TTD', facebook: 'Facebook',
};

// ─── Budget units are PER-PLATFORM. A DV360 copy's budget number is a count
//     of IMPRESSIONS, not currency: copy_dv360_campaign_launcher builds every
//     insertion order BUDGET_UNIT_IMPRESSIONS and reads `budget` as the
//     impression total (see its _COPY_BUDGET_UNIT). Every other copy platform
//     (Search/PMax/TTD/Facebook) really is advertiser currency. Same
//     convention as the DV360 line-item pacing below — impressions are always
//     labelled, never rendered with a `$`.
export const COPY_BUDGET_IS_IMPRESSIONS = { dv360: true };

export function copyBudgetIsImpressions(platform) {
  return COPY_BUDGET_IS_IMPRESSIONS[platform] === true;
}

/** Human-readable budget for a copy, e.g. "1,000,000 impressions (total)" for
 *  DV360 and "$5,000 (daily)" everywhere else. `fallback` is returned for a
 *  blank/absent amount. budgetType is passed through verbatim ('daily'/'total'). */
export function formatCopyBudget(platform, budget, budgetType, fallback = '—', currencyCode = null) {
  if (budget === '' || budget == null) return fallback;
  const suffix = budgetType ? ` (${budgetType})` : '';
  const amount = Number(budget);
  if (copyBudgetIsImpressions(platform)) {
    const count = Number.isFinite(amount) ? amount.toLocaleString() : budget;
    return `${count} impressions${suffix}`;
  }
  // `currencyCode` is the SOURCE ACCOUNT'S billing currency, read off the built template
  // (currencyFromLauncherResult). Without it this said '$' for every account, so a EUR
  // copy that the Confirm & adjust grid correctly showed as €50/day was announced as
  // "$50 (daily)" on the very next screen. Falls back to fmtCurrency's '$' when the
  // template states no code — same fallback the grid uses, so the two screens agree
  // either way rather than disagreeing.
  if (!Number.isFinite(amount)) return `$${budget}${suffix}`;
  return `${fmtMoney(amount, currencyCode)}${suffix}`;
}

// ─── REAL: resolve a preselected campaign id into the summary this workflow
//     needs (name, destination/platform, advertiser/customer id, budget). ──
export async function fetchSourceCampaign(campaignId, { accountId, userId } = {}) {
  const params = {};
  if (accountId) params.accountId = accountId;
  if (userId) params.userId = userId;
  const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}`), { params });
  return response.data;
}

export async function fetchSourceCampaigns(campaignIds, ctx) {
  const ids = Array.from(new Set((campaignIds || []).filter(Boolean)));
  return Promise.all(ids.map((id) => fetchSourceCampaign(id, ctx)));
}

// ─── REAL: POST /api/v2/campaigns/copy — see apps/app-backend/routes/v2/
//     campaigns.js router.post('/copy', ...) for the exact contract this
//     mirrors (validateCampaignCopyRequest for the request shape, the
//     `{success, campaignId, messageId, chatId, orchestration, links}`
//     response shape below). ────────────────────────────────────────────────
export async function copyCampaign(payload) {
  const response = await api.post(getApiUrl('/api/v2/campaigns/copy'), payload);
  return response.data;
}

// ─── REAL: message-status poll — identical endpoint/params/stop-condition to
//     CampaignDetailPage.js's startMessagePolling (GET /api/v2/message/:id/
//     status, stops on completed/failed/error/timeout). One-shot fetch here;
//     the interval loop lives in CopyPushStep so it can drive UI state. ─────
export async function fetchMessageStatus(messageId, { accountId, userId } = {}) {
  const params = { accountId, userId, _t: Date.now() };
  const response = await api.get(getApiUrl(`/api/v2/message/${messageId}/status`), {
    params,
    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', Pragma: 'no-cache', Expires: '0' },
  });
  return response.data;
}
export const TERMINAL_MESSAGE_STATUSES = new Set(['completed', 'failed', 'error', 'timeout']);

/**
 * True when a PLANNING-MODE build's message has reached the state where
 * GET /planning-review can serve the built template. The backend poller flips
 * the message row to 'review' when the build lands (a status deliberately NOT
 * added to TERMINAL_MESSAGE_STATUSES above — the push step's launch poll
 * still needs the original terminal set, where 'review' would be wrong).
 * 'completed' is accepted too, defensively: it means the orchestration ran
 * all the way through (planning-review then reports alreadyLaunched), and
 * treating it as "keep polling" would spin forever on a done message.
 */
export function isBuildReady(status) {
  return status === 'review' || status === 'completed';
}

// ─── REAL: planning review — the same three routes CampaignLaunchWorkflow
//     drives its review pane with (see apps/app-backend/routes/v2/campaigns.js
//     GET/PUT /:id/planning-review, POST /:id/planning-launch). `campaignRowId`
//     is OUR campaigns-table row id for the NEW copy (POST /copy's response
//     `campaignId`), never a platform-side id. ────────────────────────────────

// GET → {campaign, launcherResult, launcherResultPath,
//        originalLauncherResultPath, platform, workflowType, alreadyLaunched,
//        orchestrationStatus}. `launcherResult` is the REAL built copy
//     template (per-platform shape — see buildCopyReviewRows in
//     copyReviewEdits.js), with `targeting_plan` / `creative_plan` / `gates`
//     riding on it as extra keys.
export async function fetchPlanningReview(campaignRowId, { accountId, userId } = {}) {
  const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaignRowId}/planning-review`), {
    params: { accountId, userId },
  });
  return response.data;
}

// PUT — persist an edited launcherResult. IMPORTANT: always send the WHOLE
// edited object (targeting_plan / creative_plan / gates keys included) — the
// backend persists it verbatim, so a partial object would DROP those plans
// from the launch. applyCopyReviewEdits deep-clones the full object, so
// passing its return value straight through is correct.
export async function savePlanningReview(campaignRowId, editedLauncherResult, { accountId, userId } = {}) {
  const response = await api.put(getApiUrl(`/api/v2/campaigns/${campaignRowId}/planning-review`), {
    accountId, userId, editedLauncherResult,
  });
  return response.data;
}

// POST — resume the paused orchestration from 'review' (fires the platform's
// own API topic). Metadata is optional: the planning-mode orchestration
// already carries channelConfig/customerId/advertiserId from the build.
// Progress is then tracked by polling the ORIGINAL copy messageId (the one
// POST /copy returned) via fetchMessageStatus until TERMINAL_MESSAGE_STATUSES.
export async function launchPlanningCampaign(campaignRowId, { accountId, userId } = {}) {
  const response = await api.post(getApiUrl(`/api/v2/campaigns/${campaignRowId}/planning-launch`), {
    accountId, userId,
  });
  return response.data;
}

// ─── REAL: POST /api/v2/campaigns/copy/intent — see apps/app-backend/routes/
//     v2/campaigns.js router.post('/copy/intent', ...) for the exact contract
//     this mirrors (validateCampaignCopyIntentRequest for the request shape —
//     instruction/sourceCampaigns/formFields, sourceCampaigns passed through
//     UNMODIFIED from CopyCampaignWorkflow.js's own `sourceCampaigns` state,
//     which is already the raw GET /:id campaign-row shape
//     mapSourceCampaignsForIntent expects to map from; the
//     `{success, messageId, chatId, links:{messageStatus}}` response shape
//     below). Callers poll the returned messageId via fetchMessageStatus
//     until TERMINAL_MESSAGE_STATUSES; on 'completed' the status response's
//     `result` field holds copy_intent_reasoning.interpret_copy_instruction's
//     full return dict, verbatim:
//       { intent, scope: {campaign_ids, hits}, ops: [{id, kind, label, hits,
//         applied, consequence, blocks}], fields: {start, end, budget,
//         budget_type}, unresolved: [...], warnings: [...], error }
//     `kind` is "toggle" (user-checkable, shown with a checkbox + consequence
//     when unchecked) or "flag" (informational, always shown, never checked).
//     IMPORTANT: `result.error` can be non-null even on a 'completed' status
//     (see resolveCampaignCopyIntentStatus in apps/app-backend/routes/v2/
//     messageStatus.js) — that's interpret_copy_instruction's own documented
//     clean "LLM unavailable, here's the manual-form fallback" result (a
//     complete, valid response — `intent: null`, `ops: []`, `fields` still
//     populated from formFields — not a failure). Only a
//     'failed'/'error'/'timeout' terminal STATUS represents an actual hard
//     failure of the request itself, and is what this function
//     throws/rejects on below. ─────────────────────────────────────────────
export async function interpretCopyInstruction(instruction, sourceCampaigns, formFields, { accountId, userId } = {}) {
  const response = await api.post(getApiUrl('/api/v2/campaigns/copy/intent'), {
    instruction,
    sourceCampaigns,
    formFields,
    accountId,
    userId,
  });
  const { messageId } = response.data;

  // Same interval-poll-until-terminal shape as CopyCampaignWorkflow.js's
  // preview-loading effect / CopyPushStep.js's `pollOne`: a `cancelled` guard
  // plus clearInterval on the first terminal tick, resolving on 'completed'
  // with the raw `result` and rejecting on any other terminal status. A
  // transient fetchMessageStatus error is swallowed (logged, not rejected)
  // so the next tick simply retries, matching how those two callers already
  // tolerate a blip mid-poll rather than aborting the whole wait on one.
  return new Promise((resolve, reject) => {
    let cancelled = false;
    const timer = setInterval(async () => {
      if (cancelled) return;
      try {
        const statusRes = await fetchMessageStatus(messageId, { accountId, userId });
        if (cancelled) return;
        if (TERMINAL_MESSAGE_STATUSES.has(statusRes.status)) {
          cancelled = true;
          clearInterval(timer);
          if (statusRes.status === 'completed') {
            resolve(statusRes.result);
          } else {
            reject(new Error(statusRes.result?.error || `Copy instruction interpretation ${statusRes.status}`));
          }
        }
      } catch (err) {
        console.error('Error polling copy-instruction interpretation status', err);
      }
    }, 5000);
  });
}

// ─── Campaign names already in the destination account ────────────────────
//     GET /api/v2/campaigns/copy/existing-names — the real input the
//     duplicate-name gate compares against (see fetchExistingCampaignNames in
//     apps/app-backend/routes/v2/campaigns.js).
export async function fetchExistingCampaignNames(platform, { accountId } = {}) {
  const response = await api.get(getApiUrl('/api/v2/campaigns/copy/existing-names'), {
    params: { platform, ...(accountId && { accountId }) },
  });
  return response.data?.names || [];
}

// ─── Google Ads location suggestions for the step-1 Geo field ──────────────
//     POST /api/v2/campaigns/copy/google-locations (services/copyGoogleLocations.js):
//     Google's own geo target suggestions for `name`, any country, via the source
//     campaign's Google connection. Items: { resourceName, name, canonicalName,
//     type, countryCode }. `userId` is required: every campaigns route runs
//     extractUser first, which rejects a request without one (400 "User ID is
//     required") before the route is reached.
export async function fetchGoogleLocationSuggestions({ accountId, userId, channelId, name, signal }) {
  const response = await api.post(getApiUrl('/api/v2/campaigns/copy/google-locations'),
    { accountId, userId, channelId, name }, { signal });
  return response.data?.items || [];
}

// Mirrors copy_gates.py's _norm_name EXACTLY — `" ".join(name.split()).casefold()`
// — so a name this file calls unique is one the Python gate also calls unique.
// String.prototype.split(/\s+/) on a string with leading whitespace yields a
// leading empty element (Python's bare .split() does not), hence the trim first.
export function normalizeCampaignNameForCompare(name) {
  return String(name ?? '').trim().split(/\s+/).join(' ').toLowerCase();
}

// Longest campaign name the platform accepts. Google Ads enforces 255; DV360 and
// TTD are more generous, so 255 is used as the shared safe bound rather than
// inventing a per-platform table this file has no authority for.
export const CAMPAIGN_NAME_MAX = 255;

/**
 * The name a copy is pre-filled with: the source campaign's own name plus the local
 * date and time the copy was started, e.g.
 *
 *     Booyah_Prospecting_2026 2026-09-02 14:30
 *
 * WHY A TIMESTAMP. The old default was `<name> (copy)`, which is the same string every
 * time — so a second copy of one campaign collided with the first, and Google Ads rejects
 * duplicate campaign names within an account. Minute precision means two copies started
 * in the same hour differ on their own, and the duplicate-name gate rarely has to fire.
 *
 * LOCAL time, not UTC: this is a label a person reads against their own clock.
 *
 * `now` is injected rather than read inside so the caller can freeze it — the value must
 * be computed ONCE per copy and held, or the minute would tick over mid-session and the
 * name would change under the user while they were looking at it.
 *
 * Truncates the SOURCE NAME, never the timestamp, to stay inside CAMPAIGN_NAME_MAX: a
 * clipped timestamp is what would let two copies land on one name again.
 */
export function defaultCopyCampaignName(sourceName, now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} `
    + `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const base = String(sourceName ?? '').trim();
  // No source name (a row still loading, or one with a blank name): the stamp alone is
  // still a valid, unique, editable starting point — better than an empty required field.
  if (!base) return stamp;
  const room = CAMPAIGN_NAME_MAX - stamp.length - 1;
  const head = base.length > room ? base.slice(0, room).trimEnd() : base;
  return `${head} ${stamp}`;
}

/**
 * A name that doesn't collide with anything in `existingNames`.
 *
 * Returns `desired` untouched when it's already free. Otherwise appends " 2",
 * " 3", … — chosen over a timestamp or uuid because the result stays readable
 * and stable: pressing the button twice on the same state produces the same
 * name, and the user can still tell which source campaign it came from.
 * Comparison uses normalizeCampaignNameForCompare, so it agrees with the gate.
 */
export function suggestUniqueCampaignName(desired, existingNames = []) {
  const base = String(desired ?? '').trim();
  if (!base) return base;
  const taken = new Set((existingNames || []).map(normalizeCampaignNameForCompare));
  if (!taken.has(normalizeCampaignNameForCompare(base))) return base;

  // Guard the char cap by trimming the BASE, not the suffix — a truncated
  // suffix would let two different campaigns land on the same final name.
  for (let n = 2; n < 1000; n += 1) {
    const suffix = ` ${n}`;
    const room = CAMPAIGN_NAME_MAX - suffix.length;
    const candidate = `${base.length > room ? base.slice(0, room).trimEnd() : base}${suffix}`;
    if (!taken.has(normalizeCampaignNameForCompare(candidate))) return candidate;
  }
  return base;
}

/**
 * Resolve a copy_gates.py field-path string against a launcherResult.
 *
 * The path convention is documented in campaign_editor/copy_gates.py and is
 * deliberately simple: dot-separated keys with optional [n] indexes —
 * `campaigns[0].ad_groups[2].headline_texts[1]`, `campaign.displayName`,
 * `lineItems[3].displayName`, `Campaigns[0].CampaignName`. Returns undefined
 * for a path that doesn't resolve, so callers can stay conservative (an
 * unverifiable gate is kept, never silently cleared).
 */
export function getAtPath(obj, path) {
  if (obj == null || !path) return undefined;
  let current = obj;
  // 'a[0].b' → ['a', '0', 'b'] — every [n] becomes its own step.
  const steps = String(path).split('.').flatMap((seg) => {
    const m = /^([^[\]]+)((?:\[\d+\])*)$/.exec(seg);
    if (!m) return [seg];
    const idxs = (m[2].match(/\d+/g) || []);
    return [m[1], ...idxs];
  });
  for (const step of steps) {
    if (current == null) return undefined;
    current = current[step];
  }
  return current;
}

// copy_gates.py measures dv360 caps in UTF-8 BYTES, not characters — mirror
// that so a name this file calls "within limit" is one the Python gate agrees
// with (a multi-byte character counted as 1 here but 3 there would let an
// over-cap name through to a launch-time failure). Counted from code points
// rather than TextEncoder because the CRA jest environment (jsdom) doesn't
// provide TextEncoder as a global.
function utf8Length(s) {
  let bytes = 0;
  for (const ch of String(s)) {
    const cp = ch.codePointAt(0);
    bytes += cp <= 0x7f ? 1 : cp <= 0x7ff ? 2 : cp <= 0xffff ? 3 : 4;
  }
  return bytes;
}

/**
 * Re-check gate rows against the CURRENT (edited) launcherResult, client-side.
 *
 * The gates rode in on the build's launcherResult (copy_gates.py ran against
 * the template as BUILT); after the user edits the flagged field in the grid
 * and saves, the backend does not re-run the gates until launch — so this is
 * what lets the UI clear a blocker the user actually fixed. Conservative by
 * design: a row whose field path can't be resolved against the current
 * launcherResult is KEPT, never guessed away.
 *
 *   duplicate_name       — cleared when the field's current value no longer
 *                          equals the flagged value AND (when
 *                          existingCampaignNames is supplied) the replacement
 *                          is itself free in the destination account, using
 *                          the same normalization copy_gates.py's _norm_name
 *                          applies.
 *   char_cap_exceeded    — cleared when the field's current value fits
 *                          row.limit (UTF-8 bytes for dv360, chars otherwise).
 *   unconfirmed_rate_claim — cleared when the flagged claim substring is no
 *                          longer in the field's current text.
 *
 * Pure: returns a NEW {blocked, needs_decision} object, inputs untouched.
 */
export function resolveGates(gates, launcherResult, platform, existingCampaignNames = []) {
  if (!gates) return { blocked: [], needs_decision: [] };
  const taken = new Set((existingCampaignNames || [])
    .filter((n) => typeof n === 'string' && n.trim())
    .map(normalizeCampaignNameForCompare));

  const stillApplies = (row) => {
    if (!row) return false;
    const current = getAtPath(launcherResult, row.field);
    if (typeof current !== 'string' || !current) return true; // can't verify → keep

    if (row.code === 'duplicate_name') {
      if (normalizeCampaignNameForCompare(current) === normalizeCampaignNameForCompare(row.value)) {
        return true; // not renamed yet
      }
      // Renamed — but only cleared if the NEW name is actually free (renaming
      // one collision onto another must not clear the gate).
      return taken.has(normalizeCampaignNameForCompare(current));
    }
    if (row.code === 'char_cap_exceeded' && row.limit != null) {
      const length = platform === 'dv360' ? utf8Length(current) : String(current).length;
      return length > Number(row.limit);
    }
    if (row.code === 'unconfirmed_rate_claim' && row.claim) {
      return String(current).includes(String(row.claim));
    }
    return true; // unknown code → keep, the backend put it there for a reason
  };

  return {
    blocked: (gates.blocked || []).filter(stillApplies),
    needs_decision: (gates.needs_decision || []).filter(stillApplies),
  };
}

// ─── REAL: soft-delete a campaigns row — DELETE /api/v2/campaigns/:id (sets
//     deleted_at; same params shape CampaignsPage.js's delete action uses).
//     The copy workflow calls this best-effort before REBUILDING a campaign
//     whose previous planning-mode build already created a row: every POST
//     /copy creates a fresh campaigns row, so without this each retry /
//     changed-inputs rebuild leaked a superseded draft into the account's
//     campaign list forever. ─────────────────────────────────────────────────
export async function deleteCampaignRow(campaignRowId, { accountId, userId } = {}) {
  const response = await api.delete(getApiUrl(`/api/v2/campaigns/${campaignRowId}`), {
    params: { accountId, userId },
  });
  return response.data;
}

/**
 * The pure core of the duplicate_name "Use a unique name" quick-fix: compute
 * a genuinely free name and the renamed launcherResult, or say WHY it can't.
 *
 * Key property (the bug this extraction fixed): the gate row itself is PROOF
 * the flagged name collides — copy_gates.py compared it against the server's
 * own name set at build time — so gate.value (and the field's current value)
 * are ALWAYS seeded into the suggester's taken-set. Previously the taken-set
 * was only `existingNames`, which is fetched asynchronously and races the
 * build (and, before the backend excluded draft rows, contained ghosts): when
 * it was empty/stale the suggester saw no collision, returned the base name
 * unchanged, and the click was a silent no-op.
 *
 * Never silently no-ops: returns {error} instead, for the caller to surface.
 *
 * @returns {{newName: string, edited: object} | {error: string}}
 */
export function computeUniqueCopyRename({
  launcherResult, gate, existingNames = [], siblingNames = [],
} = {}) {
  if (!launcherResult || !gate?.value) {
    return { error: 'No built plan or duplicate-name gate to fix.' };
  }
  // Rename what the template says NOW, not what the gate flagged at build
  // time — the user may already have renamed the flagged value onto a
  // DIFFERENT collision (resolveGates keeps the gate for exactly that case).
  const currentName = getAtPath(launcherResult, gate.field);
  const baseName = typeof currentName === 'string' && currentName ? currentName : gate.value;

  const newName = suggestUniqueCampaignName(baseName, [
    // The gate's own evidence — guarantees at least "<name> 2" even when the
    // existing-names fetch failed, raced, or no longer lists the collision.
    gate.value, baseName,
    ...existingNames, ...siblingNames,
  ]);
  if (!newName || newName === baseName) {
    return { error: `Could not find a free name for "${baseName}".` };
  }

  const edited = renameCampaignInLauncherResult(launcherResult, newName, baseName);
  // Verify the rename actually landed in the template — a gate whose field
  // points at a name the rename helper doesn't cover must surface, not save
  // an unchanged template while claiming success.
  if (!campaignNamesFromLauncherResult(edited).includes(newName)) {
    return { error: `The plan's campaign name could not be rewritten to "${newName}".` };
  }
  return { newName, edited };
}

// ── STEP LIST ────────────────────────────────────────────────────────
// The copy flow is 3 steps. Both the rail and the container's step indices
// derive from this ONE list, so a numbering change cannot desync them.
//
// It used to be 4 for accounts that enabled a separate "Confirm what's copied"
// picker between setup and adjust. That step is GONE: confirming what comes
// along and adjusting it are now the same screen (CopyPlanStep), because the
// built plan already lists every ad group / line item the copy will create and
// deleting a row there is the real exclusion path — it edits the built template
// through the planning-review PUT, where the picker could only pre-filter ids
// before the build and then leave the user to re-read the same list again.
export const COPY_STEP_ADJUST_LABEL = 'Confirm & adjust';
export function copyStepLabels() {
  return [COPY_STEP1_LABEL, COPY_STEP_ADJUST_LABEL, 'Push'];
}

// ── TTD SEGMENT/AUDIENCE LOOKUP ──────────────────────────────────────
// Backs the cell panel's lookup-driven adds (a TTD geo segment or 3P audience
// can only be targeted by its platform id, so the user picks from results
// rather than typing a name). Same endpoint CLW's own runTtdLookup calls —
// GET /:id/lookup/{ttd-geo-segments|ttd-audiences} — which the copy grid simply
// never wired up, so every lookup-backed field stayed non-addable there.
const TTD_LOOKUP_PATHS = { 'ttd-geo': 'ttd-geo-segments', 'ttd-audience': 'ttd-audiences' };

export async function runTtdCopyLookup(campaignRowId, kind, query, { accountId, userId } = {}) {
  const path = TTD_LOOKUP_PATHS[kind];
  if (!campaignRowId || !path || !query) return [];
  try {
    const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignRowId}/lookup/${path}`), {
      params: { accountId, userId, q: query },
    });
    return Array.isArray(res?.data?.results) ? res.data.results : [];
  } catch (e) {
    throw new Error(e?.response?.data?.error || e.message || 'Lookup failed');
  }
}
